/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Gets minimum height for toast based on device and technical details.
 *
 * @param {boolean} hasTechnicalDetails - Whether toast has technical details
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {string} Minimum height in pixels
 */
export function getToastMinHeight(hasTechnicalDetails: boolean, isMobile: boolean): string {
  if (isMobile) {
    return 'auto';
  }
  return 'auto';
}

/**
 * Gets root styles for toast.
 *
 * @param {string} minHeight - Minimum height
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {object} Root styles
 */
function getRootStyles(minHeight: string, isMobile: boolean) {
  const baseStyles = {
    fontFamily: 'var(--font-family)',
    backgroundColor: 'var(--surface-primary)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-subtle)',
    minHeight: minHeight === 'auto' ? undefined : minHeight,
    position: 'relative' as const,
    overflow: 'visible' as const,
  };

  if (isMobile) {
    return {
      ...baseStyles,
      width: 'calc(100% - 2rem)',
      maxWidth: 'none',
      margin: '0 1rem',
      borderRadius: '12px',
      padding: '1rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    };
  }

  return baseStyles;
}

/**
 * Gets title styles for toast.
 *
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {object} Title styles
 */
function getTitleStyles(isMobile: boolean) {
  return {
    fontFamily: 'var(--font-family)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    fontSize: isMobile ? '1rem' : 'inherit',
    marginBottom: isMobile ? '0.5rem' : '0.5rem',
    paddingRight: '2.5rem',
  };
}

/**
 * Gets description styles for toast.
 *
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {object} Description styles
 */
function getDescriptionStyles(isMobile: boolean) {
  return {
    fontFamily: 'var(--font-family)',
    color: 'var(--text-primary)',
    fontSize: isMobile ? '0.9rem' : '0.875rem',
    lineHeight: 1.5,
    margin: 0,
    padding: 0,
    width: '100%',
  };
}

/**
 * Gets close button styles for toast.
 *
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {object} Close button styles
 */
function getCloseButtonStyles(isMobile: boolean) {
  const size = isMobile ? '44px' : '32px';
  return {
    position: 'absolute' as const,
    top: '0.5rem',
    right: '0.5rem',
    color: 'var(--text-secondary)',
    minWidth: size,
    minHeight: size,
    width: size,
    height: size,
    '&:hover': {
      backgroundColor: 'var(--surface-secondary)',
      color: 'var(--text-primary)',
    },
  };
}

/**
 * Gets toast styles configuration.
 *
 * @param {string} minHeight - Minimum height
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {object} Toast styles
 */
export function getToastStyles(minHeight: string, isMobile: boolean) {
  return {
    root: getRootStyles(minHeight, isMobile),
    title: getTitleStyles(isMobile),
    description: getDescriptionStyles(isMobile),
    closeButton: getCloseButtonStyles(isMobile),
  };
}
